import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Save, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuickRecipeForm } from '@/hooks/useQuickRecipeForm';
import IngredientManager from './IngredientManager';
import QuickRecipeFormFields from './QuickRecipeFormFields';
import RecipeSuccessDialog from './RecipeSuccessDialog';
import { Recipe } from '@/types/recipe';
import { ImageUpload } from '@/components/ui/image-upload';
import { toast } from 'sonner';

interface QuickRecipeFormProps {
  recipe?: Recipe;
  onRecipeCreated?: () => void;
  onRecipeUpdated?: () => void;
}

const QuickRecipeForm: React.FC<QuickRecipeFormProps> = ({ 
  recipe, 
  onRecipeCreated, 
  onRecipeUpdated 
}) => {
  const navigate = useNavigate();
  const [imageExplicitlyRemoved, setImageExplicitlyRemoved] = useState(false);
  
  const {
    formData,
    setFormData,
    ingredients,
    setIngredients,
    isSubmitting,
    handleSubmit,
    successData,
    handleDialogClose
  } = useQuickRecipeForm(recipe, onRecipeCreated, onRecipeUpdated);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit(e);
  };

  const isEditing = !!recipe;

  return (
    <>
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="pt-6">
          <form onSubmit={handleFormSubmit} className="space-y-6">
            <QuickRecipeFormFields 
              formData={formData}
              setFormData={setFormData}
            />

            <IngredientManager 
              ingredients={ingredients}
              setIngredients={setIngredients}
            />

            <ImageUpload
              recipeId={recipe?.id || 'temp'}
              currentImage={imageExplicitlyRemoved ? '' : (formData.userImageUrl || formData.imageUrl || recipe?.user_image_url || recipe?.storage_image_url)}
              onUserImageUploaded={(url, path) => {
                setFormData(prev => ({ ...prev, userImageUrl: url, userImagePath: path || '' }));
                setImageExplicitlyRemoved(url === '');
              }}
              onError={(error) => toast.error(error)}
              disabled={isSubmitting}
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" disabled={isSubmitting} className="flex-1 order-1">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEditing ? 'Update Quick Recipe' : 'Create Quick Recipe'}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
                className="order-2 sm:order-2"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {successData && (
        <RecipeSuccessDialog
          isOpen={!!successData}
          onOpenChange={handleDialogClose}
          recipeTitle={successData.title}
          recipeId={successData.id}
          isEditing={successData.isEditing}
        />
      )}
    </>
  );
};

export default QuickRecipeForm;
